import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { clockTime, minutesOfDay } from '@boud/contracts';
import { ApiClientError } from '../api/client';
import { listInvitablePeople } from '../api/directory';
import {
  cancelBooking,
  getBooking,
  listBookableRooms,
  respondToBooking,
  updateBooking,
  type Booking,
  type Room,
} from '../api/rooms';
import { Modal } from './Modal';
import { Icon } from '../lib/icons';
import { PeoplePicker } from './PeoplePicker';
import { useToast } from './Toasts';
import { useAuth } from '../auth/AuthProvider';
import { INVITEE_BADGE, badgeFor } from '../lib/statusStyles';
import { dateLabel, instantFor, minutesOfInstant, timeLabel } from '../lib/calendar';

/**
 * One meeting, in full — the only place a booking's detail is rendered.
 *
 * It lives here rather than in `BookingsPage` because the Dashboard opens the
 * same thing from its list of upcoming meetings, and the client's requirement is
 * that the two look *exactly* alike. Two copies of this markup would not: the
 * dashboard's own version had already drifted to a different definition list, an
 * unstyled one, and had no way to accept an invitation.
 *
 * `rooms` is a prop rather than a fetch because the calendar page already holds
 * the list; see `BookingDetailDialogById` for the caller that does not.
 */
export function BookingDetailDialog({
  booking,
  rooms,
  onClose,
  onChanged,
}: {
  booking: Booking;
  rooms: Room[];
  onClose: () => void;
  onChanged: () => void;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const { notify } = useToast();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);

  const myInvitation = booking.invitees.find((i) => i.employee_id === user?.id);

  const cancel = useMutation({
    mutationFn: () => cancelBooking(booking.id),
    onSuccess: () => {
      notify(t('bk_cancelled'));
      onChanged();
    },
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  const respond = useMutation({
    mutationFn: (response: 'accepted' | 'declined') => respondToBooking(booking.id, response),
    onSuccess: () => {
      notify(t('bk_responded'));
      onChanged();
    },
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  if (isEditing) {
    return (
      <EditBookingDialog
        booking={booking}
        rooms={rooms}
        onClose={() => setIsEditing(false)}
        onSaved={() => {
          notify(t('bk_saved'));
          onChanged();
        }}
      />
    );
  }

  if (isConfirmingCancel) {
    return (
      <Modal
        title={t('bk_cancel')}
        onClose={() => setIsConfirmingCancel(false)}
        footer={
          <>
            <button
              type="button"
              className="btn ghost"
              onClick={() => setIsConfirmingCancel(false)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn danger"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
            >
              {cancel.isPending ? t('saving') : t('confirm_del')}
            </button>
          </>
        }
      >
        <p>{t('bk_cancel_q')}</p>
      </Modal>
    );
  }

  return (
    <Modal
      wide
      title={booking.title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('close')}
          </button>

          {/*
            REQ-039 — an invitee answers from here. Shown only when this caller
            actually holds an invitation; the server refuses it either way.
          */}
          {myInvitation ? (
            <>
              <button
                type="button"
                className="btn"
                onClick={() => respond.mutate('declined')}
                disabled={respond.isPending}
              >
                {t('bk_decline')}
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => respond.mutate('accepted')}
                disabled={respond.isPending}
              >
                {t('bk_accept')}
              </button>
            </>
          ) : null}

          {/* UX only — REQ-040 is enforced server-side. */}
          {booking.can_manage ? (
            <>
              <button
                type="button"
                className="btn danger"
                onClick={() => setIsConfirmingCancel(true)}
              >
                {t('bk_cancel')}
              </button>
              <button type="button" className="btn primary" onClick={() => setIsEditing(true)}>
                {t('bk_edit')}
              </button>
            </>
          ) : null}
        </>
      }
    >
      <dl className="dl">
        <dt>{t('bk_room')}</dt>
        <dd>{booking.room_name}</dd>

        <dt>{t('bk_when')}</dt>
        <dd>
          {dateLabel(booking.date, i18n.language)} ·{' '}
          <bdi>
            {timeLabel(booking.starts_at, i18n.language)}–
            {timeLabel(booking.ends_at, i18n.language)}
          </bdi>
        </dd>

        <dt>{t('bk_organizer')}</dt>
        <dd>
          <bdi>{booking.organizer_name}</bdi>
          {/*
            The address, for a booking made outside Boud.

            It used to *be* the name, which is what an earlier change replaced.
            Kept rather than dropped, because the reason it was shown in the
            first place still holds: somebody who needs the room has to be able
            to go and ask the person who booked it. Directly under the name, on
            the client's instruction (2026-08-11) — it is how you contact them,
            so it belongs next to who they are.
          */}
          {booking.organizer_email ? (
            <div className="t-sub">
              <bdi>{booking.organizer_email}</bdi>
            </div>
          ) : null}
          {/*
            Their company, from their account — last, because it is the line you
            read only once you already know who this is. Absent when the
            organiser matches no employee, which is every booking made from a
            calendar Boud has no record of.
          */}
          {booking.organizer_company ? (
            <div className="t-sub">
              <bdi>{booking.organizer_company}</bdi>
            </div>
          ) : null}
        </dd>
      </dl>

      <div className="d-sec">{t('bk_attendees')}</div>
      {booking.invitees.length === 0 ? (
        <div className="d-body">{t('bk_invitees_none')}</div>
      ) : (
        <ul className="tl">
          {booking.invitees.map((invitee) => (
            <li key={invitee.employee_id}>
              {invitee.name}{' '}
              <span className={badgeFor(INVITEE_BADGE, invitee.status)}>
                {t(`bk_status_${invitee.status}`)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function EditBookingDialog({
  booking,
  rooms,
  onClose,
  onSaved,
}: {
  booking: Booking;
  rooms: Room[];
  onClose: () => void;
  onSaved: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const [title, setTitle] = useState(booking.title);
  const [roomId, setRoomId] = useState(booking.room_id);
  const [date, setDate] = useState(booking.date);
  const [from, setFrom] = useState(clockTime(minutesOfInstant(booking.starts_at)));
  const [to, setTo] = useState(clockTime(minutesOfInstant(booking.ends_at)));
  const [inviteeIds, setInviteeIds] = useState<string[]>(
    booking.invitees.map((i) => i.employee_id),
  );
  const [banner, setBanner] = useState<string | null>(null);

  const people = useQuery({
    queryKey: ['employees', 'invitable'],
    queryFn: listInvitablePeople,
  });

  const mutation = useMutation({
    mutationFn: () =>
      updateBooking(booking.id, {
        room_id: roomId,
        title,
        starts_at: instantFor(date, minutesOfDay(from)),
        ends_at: instantFor(date, minutesOfDay(to)),
        invitee_ids: inviteeIds,
      }),
    onSuccess: onSaved,
    onError: (error) => {
      if (error instanceof ApiClientError) {
        setBanner(error.message);
        notify(error.message, 'bad');
      }
    },
  });

  /*
   * BR-055 — the booking's own room stays selectable even after it has moved to
   * maintenance, so the organizer can still edit a meeting in it. The server
   * allows exactly this; a picker that dropped the room would make a booking
   * uneditable for a reason the user cannot see or fix.
   */
  const options = rooms.some((r) => r.id === booking.room_id)
    ? rooms
    : [{ id: booking.room_id, name: booking.room_name, capacity: 0 } as Room, ...rooms];

  // The organiser, who may not be the person editing — Reception editing
  // somebody else's booking can legitimately be an invitee to it.
  const selectable = (people.data ?? []).filter((p) => p.id !== booking.organizer_id);

  return (
    <Modal
      wide
      title={t('bk_edit')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="booking-edit-form"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('save')}
          </button>
        </>
      }
    >
      <form
        id="booking-edit-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setBanner(null);
          mutation.mutate();
        }}
      >
        {banner ? (
          <div className="login-error" role="alert">
            <Icon name="info" />
            <span>{banner}</span>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="bke-title">{t('bk_purpose')}</label>
          <input id="bke-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="bke-room">{t('bk_room')}</label>
          <select id="bke-room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            {options.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="bke-date">{t('bk_date')}</label>
          <input id="bke-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className="grid2">
          <div className="field">
            <label htmlFor="bke-from">{t('bk_from')}</label>
            <input
              id="bke-from"
              type="time"
              step={1800}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="bke-to">{t('bk_to')}</label>
            <input
              id="bke-to"
              type="time"
              step={1800}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        <PeoplePicker
          id="bke-invitees"
          label={t('bk_invite')}
          optionalNote={t('bk_invite_opt')}
          placeholder={t('bk_search_person')}
          people={selectable}
          selectedIds={inviteeIds}
          onChange={setInviteeIds}
        />
      </form>
    </Modal>
  );
}

/**
 * The same dialog, opened from an id instead of a loaded booking.
 *
 * The Dashboard's upcoming list carries a row's worth — title, room, times — and
 * the things somebody opens a meeting *for* are the organiser, their company and
 * who else is coming. Widening the dashboard response to include all of that
 * would make every page load pay for a detail nobody had asked to see yet.
 *
 * Fetching also re-authorises: the id is a hint, and `GET /bookings/{id}` answers
 * 404 for both "gone" and "not yours" (BR-006). A meeting cancelled between the
 * list loading and the click shows as unavailable rather than as stale detail.
 */
export function BookingDetailDialogById({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}): ReactElement {
  const { t } = useTranslation();

  const booking = useQuery({ queryKey: ['booking', id], queryFn: () => getBooking(id) });
  /*
   * Only for the edit dialog, and only reachable by someone who may manage the
   * booking. The key matches the calendar page's, so opening a meeting from the
   * Dashboard and then going to Room Booking does not fetch this twice.
   */
  const rooms = useQuery({ queryKey: ['bookable-rooms'], queryFn: listBookableRooms });

  if (booking.data) {
    return (
      <BookingDetailDialog
        booking={booking.data}
        rooms={rooms.data ?? []}
        onClose={onClose}
        onChanged={onChanged}
      />
    );
  }

  return (
    <Modal
      wide
      title={t('loading')}
      onClose={onClose}
      footer={
        <button type="button" className="btn ghost" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      {booking.error ? (
        <div className="empty" role="alert">
          {booking.error instanceof ApiClientError
            ? booking.error.message
            : t('login_network_error')}
        </div>
      ) : (
        <div className="empty" role="status" aria-live="polite">
          {t('loading')}
        </div>
      )}
    </Modal>
  );
}
